import { useEffect, useState } from "react";
import api, { formatEuro, formatDate, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Send, Trash2, Pencil } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminFactures() {
  const [items, setItems] = useState([]);
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [f, d, c] = await Promise.all([
        api.get("/admin/factures"),
        api.get("/admin/devis"),
        api.get("/admin/clients"),
      ]);
      setItems(f.data);
      setDevis(d.data);
      setClients(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const devisFor = (id) => devis.find((d) => d.id === id);
  const clientFor = (id) => clients.find((c) => c.id === id);

  const downloadPdf = async (id, numero) => {
    try {
      const res = await api.get(`/admin/factures/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const send = async (id) => {
    try {
      const res = await api.post(`/admin/factures/${id}/send`);
      if (res.data?.status === "sent") toast.success("Facture envoyée");
      else if (res.data?.status === "skipped") toast.info("Resend non configuré — envoi simulé.");
      else toast.error("Échec : " + (res.data?.error || "erreur"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const togglePaid = async (f) => {
    try {
      const { data } = await api.patch(`/admin/factures/${f.id}`, {
        status: f.status === "payee" ? "emise" : "payee",
      });
      setItems((s) => s.map((x) => (x.id === f.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createSolde = async (devis_id) => {
    try {
      const { data } = await api.post("/admin/factures", { devis_id, type: "solde" });
      toast.success(`Facture de solde ${data.numero} créée`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await api.delete(`/admin/factures/${id}`);
      setItems((s) => s.filter((f) => f.id !== id));
      toast.success("Facture supprimée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Group by devis for the "create solde" CTA
  // (Kept for legacy reference — workflow is handled on /admin/devis page now)
  // eslint-disable-next-line no-unused-vars
  const acceptedDevisSansSolde = devis.filter(
    (d) => d.status === "accepte" && !items.some((f) => f.devis_id === d.id && f.type === "solde"),
  );

  return (
    <div data-testid="admin-factures-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-eyebrow mb-3">Factures</p>
          <h1 className="h-display text-4xl md:text-5xl">Comptabilité.</h1>
        </div>
        <Link
          to="/admin/factures/new"
          className="bg-foreground text-background px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-medium hover:bg-primary transition-colors"
          data-testid="facture-new-btn"
        >
          + Nouvelle facture
        </Link>
      </div>

      {loading ? (
        <p className="text-serial">Chargement</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-[14px]">Aucune facture pour le moment.</p>
      ) : (
        <div className="border-t border-border" data-testid="factures-list">
          {items.map((f) => {
            const d = devisFor(f.devis_id);
            const c = clientFor(f.client_id);
            return (
              <div key={f.id} className="grid md:grid-cols-12 gap-4 py-5 border-b border-border items-start" data-testid={`facture-row-${f.id}`}>
                <div className="md:col-span-2">
                  <p className="text-serial">{f.numero}</p>
                  <p className="text-[12px] text-muted-foreground mt-1">{formatDate(f.created_at)}</p>
                </div>
                <div className="md:col-span-4">
                  <p className="text-[12px] tracking-[0.18em] uppercase text-primary mb-1">{c?.nom || "—"}</p>
                  <p className="font-serif text-xl leading-tight">{f.titre || d?.titre || "—"}</p>
                  <p className="text-[12px] text-muted-foreground mt-1">
                    {f.type === "acompte"
                      ? `Acompte ${f.pourcentage}%`
                      : f.type === "solde"
                        ? `Solde ${f.pourcentage}%`
                        : "Facture directe"}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <button
                    onClick={() => togglePaid(f)}
                    className={`text-eyebrow ${f.status === "payee" ? "text-primary" : "text-muted-foreground"} link-underline-static`}
                    data-testid={`facture-status-${f.id}`}
                  >
                    {f.status === "payee" ? "Payée" : f.status === "annulee" ? "Annulée" : "Émise"}
                  </button>
                </div>
                <div className="md:col-span-2 text-right">
                  <p className="font-serif text-xl leading-tight">{formatEuro(f.montant_ttc)}</p>
                  <p className="text-[11px] text-muted-foreground">TTC</p>
                </div>
                <div className="md:col-span-2 flex items-start justify-end gap-3 text-muted-foreground">
                  <Link
                    to={`/admin/factures/${f.id}`}
                    title="Modifier les lignes"
                    className="hover:text-primary"
                    data-testid={`facture-edit-${f.id}`}
                  >
                    <Pencil size={16} />
                  </Link>
                  <button title="PDF" onClick={() => downloadPdf(f.id, f.numero)} className="hover:text-foreground" data-testid={`facture-pdf-${f.id}`}>
                    <FileDown size={16} />
                  </button>
                  <button title="Envoyer" onClick={() => send(f.id)} className="hover:text-primary" data-testid={`facture-send-${f.id}`}>
                    <Send size={16} />
                  </button>
                  <button title="Supprimer" onClick={() => remove(f.id)} className="hover:text-destructive" data-testid={`facture-delete-${f.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
